/**
 * A card going from a hand to the table, as one continuous movement.
 *
 * It used to be two unrelated things: the card vanished from its slot in the
 * fan, and a *different* element appeared a fixed distance below the middle of
 * the trick and slid up. A card played from the left end of the hand therefore
 * jumped half the width of the screen between one frame and the next, which is
 * the stutter — no easing curve can hide a teleport. Trickster never has the
 * problem because its card is one element that is simply re-positioned; ours
 * lives in two different trees (the hand band, the felt), so the join is made
 * the FLIP way instead:
 *
 *   First   the fan card's box is read the moment it leaves the hand — its
 *           own ref detaching, which React does before it removes the node,
 *           so the box is still the one on screen, lift and all;
 *   Last    the trick card mounts in its resting place;
 *   Invert  it is moved, turned and scaled back onto that first box;
 *   Play    and let go, with the Web Animations API, so the flight runs on the
 *           compositor rather than as a keyframe re-resolved on every frame.
 *
 * Another seat's card starts from that seat's own fan of backs, at that fan's
 * size and turned the way that fan is turned, so it is seen to come out of
 * their hand rather than out of the felt.
 */
import type { CardId } from '@cards/shared'
import { prefersReducedMotion } from '../settings/settings'
import type { Position } from './view'
import { probe } from '../debug/probe'

interface Origin {
  /** Centre of the card on screen, in viewport pixels. */
  readonly x: number
  readonly y: number
  /** Its untransformed width, so the flight can start at the size it was. */
  readonly w: number
  /** Its rotation in the fan, in degrees. */
  readonly angle: number
  readonly at: number
}

/** An origin is only good for the flight it was taken for. A card that left
 *  the hand for any other reason — the screen changed, the hand was thrown in —
 *  must not be picked up by the next trick card with the same face. */
const ORIGIN_TTL_MS = 1500

const origins = new Map<CardId, Origin>()

/** Trick cards already thrown in, so a second call is a no-op. */
const flown = new WeakSet<HTMLElement>()

/** Called by the fan as a card leaves it. */
export function noteOrigin(card: CardId, el: HTMLElement, angle: number): void {
  // The lifted face, not the slot: a legal card sits `--card-lift` above its
  // box and the card that was just tapped higher still.
  const face = (el.querySelector('.card-lift') as HTMLElement | null) ?? el
  const r = face.getBoundingClientRect()
  if (!r.width) return
  // A card thrown by hand (touch.ts) leaves at the lean the finger gave it,
  // which is written on the face as `rotate`, outside the fan's own angle.
  // Computed, not the inline value: a thrown card is mid-animation as it
  // leaves (touch.ts `carryOn`), and only the computed value says where.
  const lean = parseFloat(getComputedStyle(face).rotate) || 0
  origins.set(card, { x: r.left + r.width / 2, y: r.top + r.height / 2, w: el.offsetWidth, angle: angle + lean, at: now() })
  probe('noteOrigin', { card, x: Math.round(r.left + r.width / 2), y: Math.round(r.top + r.height / 2), anims: face.getAnimations?.().length ?? null })
}

function takeOrigin(card: CardId): Origin | null {
  const o = origins.get(card)
  origins.delete(card)
  if (!o || now() - o.at > ORIGIN_TTL_MS) return null
  return o
}

/** Where another player let a card go that they had picked up and thrown
 *  (RemoteHands.tsx), by the position they sit at on this screen. The card
 *  they play next then flies from there — out of their hand, along the path
 *  their finger took — rather than from the middle of their fan. */
const remoteOrigins = new Map<Position, Origin>()

export function noteRemoteOrigin(position: Position, el: HTMLElement, angle: number): void {
  const r = el.getBoundingClientRect()
  if (!r.width) return
  remoteOrigins.set(position, { x: r.left + r.width / 2, y: r.top + r.height / 2, w: el.offsetWidth, angle, at: now() })
}

function takeRemoteOrigin(position: Position): Origin | null {
  const o = remoteOrigins.get(position)
  remoteOrigins.delete(position)
  if (!o || now() - o.at > ORIGIN_TTL_MS) return null
  return o
}

/** The fan of backs a seat is holding, or the seat itself once that fan is
 *  gone — a seat that has just played its last card has no fan left to throw
 *  from, and its name is the next best place. */
function seatOrigin(position: Position, from: HTMLElement): Origin | null {
  const ring = from.closest('.ring')
  if (!ring) return null
  const fan = ring.querySelector<HTMLElement>(`.seat-${position} .seat-fan-cards`)
  const img = fan?.querySelector('img')
  const el = fan ?? ring.querySelector<HTMLElement>(`.seat-${position}`)
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (!r.width && !r.height) return null
  const angle = !fan ? 0 : position === 'w' ? 90 : position === 'e' ? -90 : 0
  return { x: r.left + r.width / 2, y: r.top + r.height / 2, w: img?.offsetWidth ?? 20, angle, at: now() }
}

/** Your own card when the fan did not see it go — played for you on a timeout,
 *  or after a remount. It still comes up out of the hand, from its middle. */
function handOrigin(): Origin | null {
  const hand = document.querySelector<HTMLElement>('.hand-band .hand')
  if (!hand) return null
  const r = hand.getBoundingClientRect()
  return { x: r.left + r.width / 2, y: r.top + r.height * 0.55, w: 60, angle: 0, at: now() }
}

/**
 * Throw `el` — a trick card already sitting where it will lie — in from wherever
 * `card` came from. `tilt` is the slant it lies at, so the turn it makes in the
 * air ends exactly there.
 */
export function throwIn(el: HTMLElement, card: CardId, position: Position, tilt: number): void {
  if (typeof el.animate !== 'function') return
  // Once per card on the table. React runs a new component's layout effect
  // twice in development, and the second throw — its origin already taken —
  // fell back to the middle of the hand and replaced the first, so a card let
  // go over the table was seen to jump back into the hand and fly from there.
  if (flown.has(el)) return
  flown.add(el)
  const mine = position === 's' ? takeOrigin(card) : null
  if (prefersReducedMotion()) return
  const thrown = position === 's' ? null : takeRemoteOrigin(position)
  const origin = mine ?? thrown ?? (position === 's' ? handOrigin() : seatOrigin(position, el))
  probe('throwIn', { card, mine: !!mine, origin: origin ? [Math.round(origin.x), Math.round(origin.y)] : null })
  if (!origin) return
  const duration = durationMs('--dur-throw')
  if (duration <= 0) return

  const r = el.getBoundingClientRect()
  const dx = origin.x - (r.left + r.width / 2)
  const dy = origin.y - (r.top + r.height / 2)
  const scale = origin.w / (el.offsetWidth || origin.w)
  // The individual transform properties, not `transform`: they compose *outside*
  // the card's own transform (its tilt, and later the sweep), so the flight
  // never has to restate where the card lies — it only says where it started.
  el.animate(
    [
      { translate: `${dx.toFixed(1)}px ${dy.toFixed(1)}px`, rotate: `${(origin.angle - tilt).toFixed(2)}deg`, scale: scale.toFixed(3) },
      { translate: '0px 0px', rotate: '0deg', scale: '1' },
    ],
    { duration, easing: 'cubic-bezier(0.18, 0.90, 0.28, 1.00)' },
  )
}

function durationMs(token: string): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim()
  const n = parseFloat(raw)
  if (!Number.isFinite(n)) return 0
  return raw.endsWith('ms') ? n : raw.endsWith('s') ? n * 1000 : n
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}
